import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Badge, Spinner, Button, Table, Form, InputGroup } from "react-bootstrap";
import { Line, Pie } from "react-chartjs-2";
import { Chart as ChartJS, ArcElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler } from "chart.js";
import * as XLSX from "xlsx";
import {
  FaTicketAlt, FaHeadset, FaClock, FaCheckCircle, FaClipboardList, FaArrowRight, FaEye,
  FaPlus, FaExclamationCircle, FaSyncAlt, FaInbox, FaExclamationTriangle, FaHourglassHalf,
  FaPercentage, FaTags, FaChartLine, FaFileCsv, FaFileExcel, FaSave, FaFolderOpen, FaBell,
  FaWifi, FaEllipsisH,
} from "react-icons/fa";
import { AuthContext } from "../../context/AuthContext";
import Can from "../../components/Can";
import useMyTicketsDashboard from "../../hooks/useMyTicketsDashboard";
import { StatusPill, PriorityPill } from "../tasks/StatusChips";
import { getTatInfo, formatDuration } from "../tasks/tatUtils";
import "../tasks/PortalHomeCards.css";
import "./DataAnalystDashboard.css";

ChartJS.register(ArcElement, LineElement, PointElement, CategoryScale, LinearScale, Tooltip, Legend, Filler);

// Data Analyst home dashboard. Same two never-mixed data sources as before
// (see hooks/useMyTicketsDashboard.js) -- "My Tickets" (task_type <>
// IT_SUPPORT) and "My Complaints" (task_type = IT_SUPPORT) -- plus the
// hook's new additive `allTickets`/`allComplaints` (full, up-to-200-row
// lists) that power every analytical section below. `recentTickets`/
// `recentComplaints`/`ticketCounts`/`complaintCounts` are intentionally
// left untouched/unused here so DeveloperDashboard.jsx (which still reads
// those) is unaffected by anything in this file.
const SAVE_VIEW_KEY = "da_dashboard_saved_view_v1";
const DONE_STATUSES = new Set(["completed", "closed"]);
const CANCELLED_STATUSES = new Set(["cancelled", "rejected"]);
const PRIORITY_ORDER = { critical: 0, high: 1, medium: 2, low: 3 };
const RECENT_PAGE_SIZE = 8;

function greeting() {
  const h = new Date().getHours();
  if (h < 12) return "Good Morning";
  if (h < 17) return "Good Afternoon";
  return "Good Evening";
}

const normalizeStatus = (v) => String(v || "unknown").trim().toLowerCase().replace(/\s+/g, "_");
const titleCase = (v) => String(v || "-").replace(/_/g, " ").replace(/\w\S*/g, (t) => t.charAt(0).toUpperCase() + t.slice(1).toLowerCase());
const isDoneStatus = (s) => DONE_STATUSES.has(s);
const isCancelledStatus = (s) => CANCELLED_STATUSES.has(s);
const formatDate = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" }); } catch { return v; }
};
const formatDateTime = (v) => {
  if (!v) return "—";
  try { return new Date(v).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }); } catch { return v; }
};
const startOfDay = (v) => { const d = new Date(v); d.setHours(0, 0, 0, 0); return d; };
const endOfDay = (v) => { const d = new Date(v); d.setHours(23, 59, 59, 999); return d; };
const daysBetween = (a, b) => Math.max(0, Math.floor((b.getTime() - a.getTime()) / 86400000));
const isOverdue = (row, now) => {
  if (!row.due_at) return false;
  const s = normalizeStatus(row.status);
  if (isDoneStatus(s) || isCancelledStatus(s)) return false;
  const due = new Date(row.due_at);
  return !Number.isNaN(due.getTime()) && due < now;
};
const isUnclassified = (row) => !row.project_name || !row.project_module_name || !row.priority || !row.category_name;

function periodRange(period, customFrom, customTo, now) {
  if (period === "today") return [startOfDay(now), endOfDay(now)];
  if (period === "7d") { const d = new Date(now); d.setDate(d.getDate() - 6); return [startOfDay(d), endOfDay(now)]; }
  if (period === "30d") { const d = new Date(now); d.setDate(d.getDate() - 29); return [startOfDay(d), endOfDay(now)]; }
  if (period === "month") { const first = new Date(now.getFullYear(), now.getMonth(), 1); return [startOfDay(first), endOfDay(now)]; }
  if (period === "custom") return [customFrom ? startOfDay(new Date(customFrom)) : null, customTo ? endOfDay(new Date(customTo)) : null];
  return [null, null]; // "all"
}
// Immediately preceding window of the same length, used for "vs previous
// period" comparisons. Not meaningful for "all"/"custom" (no fixed length
// to mirror), so those return [null, null] and callers skip the delta.
function previousPeriodRange(period, now) {
  if (period === "today") { const d = new Date(now); d.setDate(d.getDate() - 1); return [startOfDay(d), endOfDay(d)]; }
  if (period === "7d") { const from = new Date(now); from.setDate(from.getDate() - 13); const to = new Date(now); to.setDate(to.getDate() - 7); return [startOfDay(from), endOfDay(to)]; }
  if (period === "30d") { const from = new Date(now); from.setDate(from.getDate() - 59); const to = new Date(now); to.setDate(to.getDate() - 30); return [startOfDay(from), endOfDay(to)]; }
  if (period === "month") { const first = new Date(now.getFullYear(), now.getMonth() - 1, 1); const last = new Date(now.getFullYear(), now.getMonth(), 0); return [startOfDay(first), endOfDay(last)]; }
  return [null, null];
}
function deltaLabel(current, previous) {
  if (previous === null) return null;
  if (previous === 0) return current === 0 ? null : `+${current} vs previous period`;
  const pct = Math.round(((current - previous) / previous) * 100);
  if (pct === 0) return "Same as previous period";
  return `${pct > 0 ? "+" : ""}${pct}% vs previous period`;
}

const PERIOD_OPTIONS = [["all", "All Time"], ["today", "Today"], ["7d", "Last 7 Days"], ["30d", "Last 30 Days"], ["month", "This Month"], ["custom", "Custom"]];
const TAB_OPTIONS = [["all", "All"], ["tickets", "Tickets"], ["complaints", "Complaints"], ["overdue", "Overdue"], ["high_priority", "High Priority"]];

function csvDownload(rows, headers, filename) {
  const esc = (v) => (v == null ? "" : `${v}`.includes(",") || `${v}`.includes('"') || `${v}`.includes("\n") ? `"${`${v}`.replace(/"/g, '""')}"` : `${v}`);
  const lines = [headers.map(([, label]) => label).join(","), ...rows.map((r) => headers.map(([key]) => esc(r[key])).join(","))];
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  a.click();
  URL.revokeObjectURL(a.href);
}
function excelDownload(sheets, filename) {
  const wb = XLSX.utils.book_new();
  sheets.forEach(({ name, rows }) => {
    const sheet = XLSX.utils.json_to_sheet(rows);
    XLSX.utils.book_append_sheet(wb, sheet, name.slice(0, 31));
  });
  XLSX.writeFile(wb, filename);
}

export default function DataAnalystDashboard() {
  const { user } = useContext(AuthContext) || {};
  const navigate = useNavigate();
  const recentActivityRef = useRef(null);
  const tabAutoSetRef = useRef(false);

  const {
    allTickets, allComplaints, allLoading, allError,
    reviewFormsCount, reviewFormsLoading, refresh,
  } = useMyTicketsDashboard();

  const [lastUpdated, setLastUpdated] = useState(new Date());
  useEffect(() => { if (!allLoading) setLastUpdated(new Date()); }, [allLoading]);
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener("online", on);
    window.addEventListener("offline", off);
    return () => { window.removeEventListener("online", on); window.removeEventListener("offline", off); };
  }, []);

  /* ── Global filter bar (section 5) ── */
  const [filterPeriod, setFilterPeriod] = useState("all");
  const [filterCustomFrom, setFilterCustomFrom] = useState("");
  const [filterCustomTo, setFilterCustomTo] = useState("");
  const [filterStatus, setFilterStatus] = useState("ALL");
  const [filterPriority, setFilterPriority] = useState("ALL");
  const [filterProject, setFilterProject] = useState("ALL");
  const [filterModule, setFilterModule] = useState("ALL");
  const [filterCategory, setFilterCategory] = useState("ALL");
  const [filterAssignee, setFilterAssignee] = useState("ALL");
  const [savedViewExists, setSavedViewExists] = useState(() => { try { return !!localStorage.getItem(SAVE_VIEW_KEY); } catch { return false; } });
  const [saveViewNotice, setSaveViewNotice] = useState("");

  const resetFilters = () => {
    setFilterPeriod("all"); setFilterCustomFrom(""); setFilterCustomTo("");
    setFilterStatus("ALL"); setFilterPriority("ALL"); setFilterProject("ALL");
    setFilterModule("ALL"); setFilterCategory("ALL"); setFilterAssignee("ALL");
  };
  const saveView = () => {
    const view = { filterPeriod, filterCustomFrom, filterCustomTo, filterStatus, filterPriority, filterProject, filterModule, filterCategory, filterAssignee };
    try { localStorage.setItem(SAVE_VIEW_KEY, JSON.stringify(view)); setSavedViewExists(true); } catch { /* storage unavailable -- silently skip, nothing else depends on this */ }
    setSaveViewNotice("View saved");
    setTimeout(() => setSaveViewNotice(""), 2000);
  };
  const loadSavedView = () => {
    try {
      const raw = localStorage.getItem(SAVE_VIEW_KEY);
      if (!raw) return;
      const v = JSON.parse(raw);
      setFilterPeriod(v.filterPeriod ?? "all"); setFilterCustomFrom(v.filterCustomFrom ?? ""); setFilterCustomTo(v.filterCustomTo ?? "");
      setFilterStatus(v.filterStatus ?? "ALL"); setFilterPriority(v.filterPriority ?? "ALL"); setFilterProject(v.filterProject ?? "ALL");
      setFilterModule(v.filterModule ?? "ALL"); setFilterCategory(v.filterCategory ?? "ALL"); setFilterAssignee(v.filterAssignee ?? "ALL");
    } catch { /* corrupt/unavailable storage -- ignore, filters stay as-is */ }
  };
  const hasActiveFilters = filterPeriod !== "all" || filterStatus !== "ALL" || filterPriority !== "ALL" || filterProject !== "ALL" || filterModule !== "ALL" || filterCategory !== "ALL" || filterAssignee !== "ALL";

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const now = useMemo(() => new Date(), [allTickets, allComplaints]);

  const allItems = useMemo(() => [
    ...allTickets.map((t) => ({ ...t, itemType: "ticket", status: normalizeStatus(t.status) })),
    ...allComplaints.map((c) => ({ ...c, itemType: "complaint", status: normalizeStatus(c.status) })),
  ], [allTickets, allComplaints]);

  const projectOptions = useMemo(() => Array.from(new Set(allItems.map((i) => i.project_name).filter(Boolean))).sort(), [allItems]);
  const moduleOptions = useMemo(() => Array.from(new Set(allItems.map((i) => i.project_module_name).filter(Boolean))).sort(), [allItems]);
  const categoryOptions = useMemo(() => Array.from(new Set(allItems.map((i) => i.category_name).filter(Boolean))).sort(), [allItems]);
  const assigneeOptions = useMemo(() => Array.from(new Set(allItems.map((i) => i.assigned_to_name).filter(Boolean))).sort(), [allItems]);
  const statusOptionsPresent = useMemo(() => Array.from(new Set(allItems.map((i) => i.status))).sort(), [allItems]);

  const globalFiltered = useMemo(() => {
    const [from, to] = periodRange(filterPeriod, filterCustomFrom, filterCustomTo, now);
    return allItems.filter((it) => {
      if (from || to) {
        const created = it.created_at ? new Date(it.created_at) : null;
        if (!created || Number.isNaN(created.getTime())) return false;
        if (from && created < from) return false;
        if (to && created > to) return false;
      }
      if (filterStatus !== "ALL" && it.status !== filterStatus) return false;
      if (filterPriority !== "ALL" && String(it.priority || "").toLowerCase() !== filterPriority) return false;
      if (filterProject !== "ALL" && (it.project_name || "Unclassified") !== filterProject) return false;
      if (filterModule !== "ALL" && (it.project_module_name || "Unclassified") !== filterModule) return false;
      if (filterCategory !== "ALL" && (it.category_name || "Unclassified") !== filterCategory) return false;
      if (filterAssignee !== "ALL" && (it.assigned_to_name || "Unassigned") !== filterAssignee) return false;
      return true;
    });
  }, [allItems, filterPeriod, filterCustomFrom, filterCustomTo, filterStatus, filterPriority, filterProject, filterModule, filterCategory, filterAssignee, now]);

  const previousPeriodItems = useMemo(() => {
    const [from, to] = previousPeriodRange(filterPeriod, now);
    if (!from && !to) return null;
    return allItems.filter((it) => { const c = it.created_at ? new Date(it.created_at) : null; return c && !Number.isNaN(c.getTime()) && c >= from && c <= to; });
  }, [allItems, filterPeriod, now]);

  /* ── KPI row 1 ── */
  const totalActiveCount = useMemo(() => globalFiltered.filter((i) => !isDoneStatus(i.status) && !isCancelledStatus(i.status)).length, [globalFiltered]);
  const openComplaintsCount = useMemo(() => globalFiltered.filter((i) => i.itemType === "complaint" && !isDoneStatus(i.status) && !isCancelledStatus(i.status)).length, [globalFiltered]);
  const pendingCount = useMemo(() => globalFiltered.filter((i) => i.status === "pending").length, [globalFiltered]);
  const overdueCount = useMemo(() => globalFiltered.filter((i) => isOverdue(i, now)).length, [globalFiltered, now]);
  const slaAtRiskCount = useMemo(() => globalFiltered.filter((i) => !isDoneStatus(i.status) && !isCancelledStatus(i.status) && getTatInfo(i, now).tone === "amber").length, [globalFiltered, now]);
  const completedCount = useMemo(() => globalFiltered.filter((i) => isDoneStatus(i.status)).length, [globalFiltered]);
  const nonCancelledCount = useMemo(() => globalFiltered.filter((i) => !isCancelledStatus(i.status)).length, [globalFiltered]);
  const resolutionRate = nonCancelledCount ? Math.round((completedCount / nonCancelledCount) * 100) : 0;

  const previousOpenComplaints = previousPeriodItems ? previousPeriodItems.filter((i) => i.itemType === "complaint" && !isDoneStatus(i.status) && !isCancelledStatus(i.status)).length : null;
  const previousResolutionRate = useMemo(() => {
    if (!previousPeriodItems) return null;
    const nonCancelled = previousPeriodItems.filter((i) => !isCancelledStatus(i.status));
    if (!nonCancelled.length) return 0;
    return Math.round((previousPeriodItems.filter((i) => isDoneStatus(i.status)).length / nonCancelled.length) * 100);
  }, [previousPeriodItems]);

  /* ── KPI row 2 ── */
  const resolutionDurations = useMemo(() => globalFiltered
    .filter((i) => isDoneStatus(i.status) && i.created_at && (i.completed_at || i.updated_at))
    .map((i) => new Date(i.completed_at || i.updated_at).getTime() - new Date(i.created_at).getTime())
    .filter((ms) => Number.isFinite(ms) && ms >= 0), [globalFiltered]);
  const avgResolutionMs = resolutionDurations.length ? resolutionDurations.reduce((a, b) => a + b, 0) / resolutionDurations.length : null;
  const medianResolutionMs = useMemo(() => {
    if (!resolutionDurations.length) return null;
    const sorted = [...resolutionDurations].sort((a, b) => a - b);
    const mid = Math.floor(sorted.length / 2);
    return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
  }, [resolutionDurations]);
  const overdueRate = globalFiltered.length ? Math.round((overdueCount / globalFiltered.length) * 100) : 0;
  const unclassifiedCount = useMemo(() => globalFiltered.filter(isUnclassified).length, [globalFiltered]);
  const unclassifiedPct = globalFiltered.length ? Math.round((unclassifiedCount / globalFiltered.length) * 100) : 0;

  const kpiRow1 = [
    { key: "total", label: "Total Active Items", value: totalActiveCount, sub: "Tickets + complaints not yet closed", tone: "blue", Icon: FaClipboardList, tab: "all" },
    { key: "complaints", label: "Open Complaints", value: openComplaintsCount, sub: deltaLabel(openComplaintsCount, previousOpenComplaints) || "Currently open", tone: "teal", Icon: FaHeadset, tab: "complaints" },
    { key: "pending", label: "Pending", value: pendingCount, sub: "Awaiting action", tone: "amber", Icon: FaClock, tab: "all", statusFilter: "pending" },
    { key: "overdue", label: "Overdue", value: overdueCount, sub: "Past due date", tone: overdueCount > 0 ? "red" : "green", Icon: FaExclamationTriangle, tab: "overdue" },
    { key: "sla", label: "SLA At Risk", value: slaAtRiskCount, sub: "Due within 2 hours", tone: slaAtRiskCount > 0 ? "amber" : "green", Icon: FaHourglassHalf, tab: "all" },
    { key: "resolution", label: "Resolution Rate", value: `${resolutionRate}%`, sub: deltaLabel(resolutionRate, previousResolutionRate) || "Completed vs non-cancelled", tone: "green", Icon: FaPercentage, tab: "all" },
  ];
  const kpiRow2 = [
    { key: "avg_res", label: "Average Resolution Time", value: avgResolutionMs === null ? "—" : formatDuration(avgResolutionMs), sub: `Based on ${resolutionDurations.length} resolved item(s)`, tone: "purple", Icon: FaHourglassHalf },
    { key: "completed", label: "Completed This Period", value: completedCount, sub: "Marked completed / closed", tone: "green", Icon: FaCheckCircle },
    { key: "overdue_rate", label: "Overdue Rate", value: `${overdueRate}%`, sub: "Of items in the current view", tone: overdueRate > 0 ? "amber" : "green", Icon: FaExclamationTriangle },
    { key: "unclassified", label: "Unclassified Records", value: unclassifiedCount, sub: `${unclassifiedPct}% missing a classification field`, warn: unclassifiedPct > 20, tone: unclassifiedPct > 20 ? "amber" : "blue", Icon: FaTags },
  ];

  /* ── Needs Your Attention (section 7) ── */
  const missingProjectCount = useMemo(() => globalFiltered.filter((i) => !i.project_name).length, [globalFiltered]);
  const pendingOver3Days = useMemo(() => globalFiltered.filter((i) => i.status === "pending" && i.created_at && daysBetween(new Date(i.created_at), now) > 3).length, [globalFiltered, now]);
  const missingPriorityCount = useMemo(() => globalFiltered.filter((i) => !i.priority).length, [globalFiltered]);

  const trendingCategory = useMemo(() => {
    if (!previousPeriodItems) return null;
    const curr = new Map();
    globalFiltered.forEach((i) => { const k = i.category_name || "Unclassified"; curr.set(k, (curr.get(k) || 0) + 1); });
    const prev = new Map();
    previousPeriodItems.forEach((i) => { const k = i.category_name || "Unclassified"; prev.set(k, (prev.get(k) || 0) + 1); });
    let best = null;
    curr.forEach((count, key) => {
      if (key === "Unclassified" || count < 3) return;
      const prevCount = prev.get(key) || 0;
      const growth = count - prevCount;
      if (growth > 0 && (!best || growth > best.growth)) best = { key, count, prevCount, growth };
    });
    return best;
  }, [globalFiltered, previousPeriodItems]);

  const [unclassifiedOnly, setUnclassifiedOnly] = useState(false);
  const [activityTab, setActivityTab] = useState("all");
  const jumpToActivity = () => recentActivityRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const attentionItems = useMemo(() => {
    const items = [];
    if (overdueCount > 0) items.push({ key: "overdue", icon: "fa-triangle-exclamation", tone: "danger", text: `${overdueCount} item${overdueCount === 1 ? " is" : "s are"} overdue`, action: () => { setActivityTab("overdue"); jumpToActivity(); } });
    if (missingPriorityCount > 0) items.push({ key: "priority", icon: "fa-tag", tone: "warning", text: `${missingPriorityCount} record${missingPriorityCount === 1 ? "" : "s"} ${missingPriorityCount === 1 ? "is" : "are"} missing priority`, action: () => { setUnclassifiedOnly(true); jumpToActivity(); } });
    if (missingProjectCount > 0) items.push({ key: "project", icon: "fa-folder", tone: "warning", text: `${missingProjectCount} record${missingProjectCount === 1 ? "" : "s"} ${missingProjectCount === 1 ? "is" : "are"} missing project classification`, action: () => { setUnclassifiedOnly(true); jumpToActivity(); } });
    if (pendingOver3Days > 0) items.push({ key: "aging", icon: "fa-hourglass-half", tone: "warning", text: `${pendingOver3Days} issue${pendingOver3Days === 1 ? " has" : "s have"} remained pending for more than 3 days`, action: () => { setActivityTab("all"); jumpToActivity(); } });
    if (trendingCategory) items.push({ key: "trend", icon: "fa-arrow-trend-up", tone: "info", text: `${trendingCategory.key}-related issues have increased this period (${trendingCategory.prevCount} → ${trendingCategory.count})`, action: () => setFilterCategory(trendingCategory.key) });
    return items;
  }, [overdueCount, missingPriorityCount, missingProjectCount, pendingOver3Days, trendingCategory]);

  /* ── Workload Trend (section 8) ── */
  const [trendGranularity, setTrendGranularity] = useState("weekly");
  const trendChart = useMemo(() => {
    const bucketCount = trendGranularity === "daily" ? 14 : trendGranularity === "weekly" ? 12 : 12;
    const bucketMs = trendGranularity === "daily" ? 86400000 : trendGranularity === "weekly" ? 7 * 86400000 : null;
    const buckets = [];
    if (trendGranularity === "monthly") {
      for (let i = bucketCount - 1; i >= 0; i--) {
        const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
        buckets.push({ label: d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" }), from: startOfDay(d), to: endOfDay(new Date(d.getFullYear(), d.getMonth() + 1, 0)) });
      }
    } else {
      for (let i = bucketCount - 1; i >= 0; i--) {
        const to = new Date(now.getTime() - i * bucketMs);
        const from = trendGranularity === "daily" ? to : new Date(to.getTime() - 6 * 86400000);
        buckets.push({ label: trendGranularity === "daily" ? to.toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : `${from.toLocaleDateString("en-IN", { day: "2-digit", month: "short" })}`, from: startOfDay(from), to: endOfDay(to) });
      }
    }
    const inBucket = (dateVal, bucket) => { if (!dateVal) return false; const d = new Date(dateVal); return !Number.isNaN(d.getTime()) && d >= bucket.from && d <= bucket.to; };
    const hasTickets = allTickets.length > 0;
    const ticketsCreated = buckets.map((b) => globalFiltered.filter((i) => i.itemType === "ticket" && inBucket(i.created_at, b)).length);
    const complaintsCreated = buckets.map((b) => globalFiltered.filter((i) => i.itemType === "complaint" && inBucket(i.created_at, b)).length);
    const resolved = buckets.map((b) => globalFiltered.filter((i) => isDoneStatus(i.status) && inBucket(i.completed_at || i.updated_at, b)).length);
    const datasets = [];
    if (hasTickets) datasets.push({ label: "Tickets Created", data: ticketsCreated, borderColor: "#2563eb", backgroundColor: "rgba(37,99,235,0.12)", fill: true, tension: 0.35 });
    datasets.push({ label: "Complaints Created", data: complaintsCreated, borderColor: "#0f766e", backgroundColor: "rgba(15,118,110,0.12)", fill: true, tension: 0.35 });
    datasets.push({ label: "Resolved", data: resolved, borderColor: "#15803d", backgroundColor: "rgba(21,128,61,0.12)", fill: true, tension: 0.35 });
    return { labels: buckets.map((b) => b.label), datasets, hasAnyData: ticketsCreated.some(Boolean) || complaintsCreated.some(Boolean) || resolved.some(Boolean) };
  }, [globalFiltered, allTickets.length, trendGranularity, now]);
  const trendChartOpts = { responsive: true, maintainAspectRatio: false, interaction: { mode: "index", intersect: false }, plugins: { legend: { position: "bottom", labels: { boxWidth: 10, font: { size: 10.5 }, padding: 10 } } }, scales: { y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } } }, x: { ticks: { font: { size: 10 } } } } };

  /* ── Status Breakdown donut (section 8 right) ── */
  const statusRows = useMemo(() => {
    const map = new Map();
    globalFiltered.forEach((i) => map.set(i.status, (map.get(i.status) || 0) + 1));
    return [...map.entries()].map(([key, count]) => ({ key, label: titleCase(key), count }));
  }, [globalFiltered]);
  const statusTotal = statusRows.reduce((s, r) => s + r.count, 0);
  const STATUS_COLOR = { open: "#2563eb", assigned: "#2563eb", pending: "#d97706", in_progress: "#2563eb", completed: "#15803d", closed: "#15803d", cancelled: "#94a3b8", rejected: "#94a3b8" };
  const donutData = { labels: statusRows.map((r) => r.label), datasets: [{ data: statusRows.map((r) => r.count), backgroundColor: statusRows.map((r) => STATUS_COLOR[r.key] || "#94a3b8"), borderWidth: 2, borderColor: "#fff" }] };
  const donutOpts = { responsive: true, maintainAspectRatio: false, cutout: "68%", plugins: { legend: { display: false }, tooltip: { callbacks: { label: (ctx) => ` ${ctx.label}: ${ctx.raw} (${statusTotal ? Math.round((ctx.raw / statusTotal) * 100) : 0}%)` } } } };

  /* ── Recurring Issues (9) + Issues by Category (10) -- same grouping ── */
  const categoryRows = useMemo(() => {
    const currMap = new Map();
    globalFiltered.forEach((i) => { const k = i.category_name || "Unclassified"; currMap.set(k, (currMap.get(k) || 0) + 1); });
    const prevMap = new Map();
    (previousPeriodItems || []).forEach((i) => { const k = i.category_name || "Unclassified"; prevMap.set(k, (prevMap.get(k) || 0) + 1); });
    const total = globalFiltered.length || 1;
    return [...currMap.entries()]
      .map(([name, count]) => {
        const prevCount = prevMap.get(name) || 0;
        const trendPct = previousPeriodItems ? (prevCount ? Math.round(((count - prevCount) / prevCount) * 100) : (count ? 100 : 0)) : null;
        return { key: name, name, count, pct: Math.round((count / total) * 100), trendPct };
      })
      .sort((a, b) => b.count - a.count);
  }, [globalFiltered, previousPeriodItems]);
  const recurringIssues = categoryRows.filter((r) => r.key !== "Unclassified").slice(0, 5);

  /* ── SLA & Aging (11) ── */
  const openItems = useMemo(() => globalFiltered.filter((i) => !isDoneStatus(i.status) && !isCancelledStatus(i.status)), [globalFiltered]);
  const agingBuckets = useMemo(() => {
    const buckets = [{ key: "lt24h", label: "< 24h", min: 0, max: 1, count: 0 }, { key: "1_3d", label: "1–3 days", min: 1, max: 3, count: 0 }, { key: "3_7d", label: "3–7 days", min: 3, max: 7, count: 0 }, { key: "7d_plus", label: "7+ days", min: 7, max: Infinity, count: 0 }];
    openItems.forEach((i) => {
      if (!i.created_at) return;
      const age = daysBetween(new Date(i.created_at), now);
      const bucket = buckets.find((b) => age >= b.min && age < b.max) || buckets[buckets.length - 1];
      bucket.count += 1;
    });
    return buckets;
  }, [openItems, now]);
  const slaComplianceRows = useMemo(() => globalFiltered.filter((i) => isDoneStatus(i.status) && i.due_at), [globalFiltered]);
  const slaCompliancePct = slaComplianceRows.length ? Math.round((slaComplianceRows.filter((i) => getTatInfo(i, now).tone === "green").length / slaComplianceRows.length) * 100) : null;
  const oldestOpenItem = useMemo(() => openItems.filter((i) => i.created_at).sort((a, b) => new Date(a.created_at) - new Date(b.created_at))[0] || null, [openItems]);

  /* ── Data Quality (12) ── */
  const missingModuleCount = useMemo(() => globalFiltered.filter((i) => !i.project_module_name).length, [globalFiltered]);
  const missingCategoryCount = useMemo(() => globalFiltered.filter((i) => !i.category_name).length, [globalFiltered]);

  /* ── Recent Activity (13) ── */
  useEffect(() => {
    if (tabAutoSetRef.current || allLoading) return;
    if (allTickets.length === 0 && allComplaints.length > 0) setActivityTab("complaints");
    tabAutoSetRef.current = true;
  }, [allLoading, allTickets.length, allComplaints.length]);

  const [activitySearch, setActivitySearch] = useState("");
  const [activitySort, setActivitySort] = useState("newest");
  const [activityPage, setActivityPage] = useState(1);
  useEffect(() => { setActivityPage(1); }, [activityTab, activitySearch, activitySort, unclassifiedOnly, globalFiltered.length]);

  const activityTabFiltered = useMemo(() => globalFiltered.filter((i) => {
    if (unclassifiedOnly && !isUnclassified(i)) return false;
    if (activityTab === "tickets") return i.itemType === "ticket";
    if (activityTab === "complaints") return i.itemType === "complaint";
    if (activityTab === "overdue") return isOverdue(i, now);
    if (activityTab === "high_priority") return ["critical", "high"].includes(String(i.priority || "").toLowerCase());
    return true;
  }), [globalFiltered, activityTab, unclassifiedOnly, now]);

  const activitySearched = useMemo(() => {
    const q = activitySearch.trim().toLowerCase();
    if (!q) return activityTabFiltered;
    return activityTabFiltered.filter((i) => [i.title, i.description, i.project_name, i.project_module_name, i.category_name, i.assigned_to_name].filter(Boolean).join(" ").toLowerCase().includes(q));
  }, [activityTabFiltered, activitySearch]);

  const activitySorted = useMemo(() => {
    const rows = [...activitySearched];
    if (activitySort === "oldest") rows.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    else if (activitySort === "priority") rows.sort((a, b) => (PRIORITY_ORDER[String(a.priority || "").toLowerCase()] ?? 9) - (PRIORITY_ORDER[String(b.priority || "").toLowerCase()] ?? 9));
    else if (activitySort === "due") rows.sort((a, b) => { const av = a.due_at ? new Date(a.due_at).getTime() : Infinity; const bv = b.due_at ? new Date(b.due_at).getTime() : Infinity; return av - bv; });
    else if (activitySort === "longest_pending") rows.sort((a, b) => new Date(a.created_at || 0) - new Date(b.created_at || 0));
    else rows.sort((a, b) => new Date(b.created_at || 0) - new Date(a.created_at || 0));
    return rows;
  }, [activitySearched, activitySort]);

  const activityTotalPages = Math.max(1, Math.ceil(activitySorted.length / RECENT_PAGE_SIZE));
  const activityPageClamped = Math.min(activityPage, activityTotalPages);
  const activityPageRows = useMemo(() => activitySorted.slice((activityPageClamped - 1) * RECENT_PAGE_SIZE, activityPageClamped * RECENT_PAGE_SIZE), [activitySorted, activityPageClamped]);

  /* ── Exports (17) ── */
  const EXPORT_HEADERS = [["id", "ID"], ["title", "Title"], ["itemType", "Type"], ["project_name", "Project"], ["project_module_name", "Module"], ["category_name", "Category"], ["priority", "Priority"], ["status", "Status"], ["created_at", "Created"], ["due_at", "Due Date"], ["assigned_to_name", "Assignee"]];
  const exportRowsForFile = () => activitySorted.map((r) => ({ ...r, project_name: r.project_name || "Unclassified", project_module_name: r.project_module_name || "Unclassified", priority: r.priority || "Unclassified", category_name: r.category_name || "Unclassified" }));
  const handleExportCsv = () => csvDownload(exportRowsForFile(), EXPORT_HEADERS, `data_analyst_activity_${Date.now()}.csv`);
  const handleExportExcel = () => excelDownload([{ name: "Activity", rows: exportRowsForFile().map((r) => Object.fromEntries(EXPORT_HEADERS.map(([k, label]) => [label, r[k]]))) }], `data_analyst_activity_${Date.now()}.xlsx`);
  const handleGenerateReport = () => {
    const kpiSheet = [...kpiRow1, ...kpiRow2].map((k) => ({ Metric: k.label, Value: k.value }));
    const categorySheet = categoryRows.map((r) => ({ Category: r.name, Count: r.count, Percentage: `${r.pct}%`, Trend: r.trendPct === null ? "—" : `${r.trendPct > 0 ? "+" : ""}${r.trendPct}%` }));
    const activitySheet = exportRowsForFile().map((r) => Object.fromEntries(EXPORT_HEADERS.map(([k, label]) => [label, r[k]])));
    excelDownload([{ name: "KPI Summary", rows: kpiSheet }, { name: "Category Breakdown", rows: categorySheet }, { name: "Recent Activity", rows: activitySheet }], `data_analyst_report_${Date.now()}.xlsx`);
  };

  const busy = allLoading;
  const initial = (user?.name || "A").trim().charAt(0).toUpperCase();
  const technicianName = user?.name || (user?.email ? user.email.split("@")[0] : "Analyst");

  return (
    <div className="unit-page tlp-page da-dashboard-theme">
      {!isOnline && (
        <div className="da-offline-banner"><FaWifi /> You're offline — some data may be out of date. Exports are disabled until you're back online.</div>
      )}
      {allError && (
        <div className="da-partial-banner"><FaExclamationCircle /> Some records could not be loaded. Analytics below may be based on partial data.</div>
      )}

      {/* Compact welcome header (section 4) */}
      <div className="tlp-hero da-hero-compact">
        <div className="tlp-hero-main">
          <div className="tlp-hero-avatar">{initial}</div>
          <div>
            <div className="tlp-hero-greeting">Data Analyst Panel</div>
            <h2 className="tlp-hero-title">{greeting()}, {technicianName}</h2>
            <p className="tlp-hero-subtitle">Monitor workload, SLA performance and issue trends.</p>
            <span className="da-last-updated">
              Last updated: {formatDateTime(lastUpdated)}
              <button type="button" className="da-refresh-icon" onClick={refresh} disabled={busy} aria-label="Refresh dashboard"><FaSyncAlt className={busy ? "da-spin" : ""} /></button>
            </span>
          </div>
        </div>
        <div className="tlp-hactions">
          <Button className="tlp-btn tlp-btn-ghost" disabled={!isOnline} onClick={handleExportCsv}>Export Data</Button>
          <Button className="tlp-btn tlp-btn-primary" disabled={!isOnline} onClick={handleGenerateReport}>Generate Report</Button>
        </div>
      </div>

      {/* Global filter bar (section 5) */}
      <div className="tlp-card da-filterbar">
        <div className="da-filterbar-row">
          <div className="da-pill-group" role="group" aria-label="Date range">
            {PERIOD_OPTIONS.map(([key, label]) => (
              <button key={key} type="button" aria-pressed={filterPeriod === key} className={`da-pill-btn${filterPeriod === key ? " da-pill-btn--active" : ""}`} onClick={() => setFilterPeriod(key)}>{label}</button>
            ))}
          </div>
          {filterPeriod === "custom" && (
            <div className="da-srow">
              <Form.Control type="date" className="da-sel" value={filterCustomFrom} onChange={(e) => setFilterCustomFrom(e.target.value)} aria-label="Custom range from" />
              <span style={{ fontSize: 12, color: "#94a3b8" }}>to</span>
              <Form.Control type="date" className="da-sel" value={filterCustomTo} onChange={(e) => setFilterCustomTo(e.target.value)} aria-label="Custom range to" />
            </div>
          )}
        </div>
        <div className="da-filterbar-row da-filterbar-row--main">
          <div className="da-filter-grid">
            <Form.Select className="da-sel" aria-label="Filter by status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
              <option value="ALL">All Status</option>
              {statusOptionsPresent.map((s) => <option key={s} value={s}>{titleCase(s)}</option>)}
            </Form.Select>
            <Form.Select className="da-sel" aria-label="Filter by priority" value={filterPriority} onChange={(e) => setFilterPriority(e.target.value)}>
              <option value="ALL">All Priority</option>
              {["critical", "high", "medium", "low"].map((p) => <option key={p} value={p}>{titleCase(p)}</option>)}
            </Form.Select>
            <Form.Select className="da-sel" aria-label="Filter by project" value={filterProject} onChange={(e) => setFilterProject(e.target.value)}>
              <option value="ALL">All Projects</option>
              {projectOptions.map((p) => <option key={p} value={p}>{p}</option>)}
            </Form.Select>
            <Form.Select className="da-sel" aria-label="Filter by module" value={filterModule} onChange={(e) => setFilterModule(e.target.value)}>
              <option value="ALL">All Modules</option>
              {moduleOptions.map((m) => <option key={m} value={m}>{m}</option>)}
            </Form.Select>
            <Form.Select className="da-sel" aria-label="Filter by category" value={filterCategory} onChange={(e) => setFilterCategory(e.target.value)}>
              <option value="ALL">All Categories</option>
              {categoryOptions.map((c) => <option key={c} value={c}>{c}</option>)}
            </Form.Select>
            <Form.Select className="da-sel" aria-label="Filter by assignee" value={filterAssignee} onChange={(e) => setFilterAssignee(e.target.value)}>
              <option value="ALL">All Assignees</option>
              {assigneeOptions.map((a) => <option key={a} value={a}>{a}</option>)}
            </Form.Select>
          </div>
          <div className="da-filter-actions">
            {hasActiveFilters && <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={resetFilters}>Reset Filters</Button>}
            <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={saveView}><FaSave style={{ fontSize: 11 }} />{saveViewNotice || "Save View"}</Button>
            {savedViewExists && <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={loadSavedView}>Load Saved View</Button>}
          </div>
        </div>
      </div>

      {/* KPI section (section 6) */}
      <div className="tlp-kpi-strip">
        {busy && !allItems.length ? Array.from({ length: 6 }).map((_, i) => <div key={i} className="da-skeleton-kpi" />) : kpiRow1.map((card) => {
          const Icon = card.Icon;
          return (
            <button type="button" className={`tlp-kpi da-kpi--clickable`} key={card.key} onClick={() => { setActivityTab(card.tab); if (card.statusFilter) setFilterStatus(card.statusFilter); jumpToActivity(); }}>
              <div className={`tlp-kpi-icon tlp-kpi-icon-${card.tone}`}><Icon /></div>
              <div><span className="tlp-kpi-label">{card.label}</span><strong className="tlp-kpi-val">{card.value}</strong><span className="da-kpi-sub">{card.sub}</span></div>
            </button>
          );
        })}
      </div>
      <div className="tlp-kpi-strip da-kpi-strip-secondary">
        {busy && !allItems.length ? Array.from({ length: 4 }).map((_, i) => <div key={i} className="da-skeleton-kpi" />) : kpiRow2.map((card) => {
          const Icon = card.Icon;
          return (
            <div className={`tlp-kpi${card.warn ? " da-kpi--warn" : ""}`} key={card.key}>
              <div className={`tlp-kpi-icon tlp-kpi-icon-${card.tone}`}><Icon /></div>
              <div><span className="tlp-kpi-label">{card.label}</span><strong className="tlp-kpi-val">{card.value}</strong><span className="da-kpi-sub">{card.sub}</span></div>
            </div>
          );
        })}
      </div>

      {/* Needs Your Attention (section 7) */}
      <div className="tlp-card">
        <div className="tlp-card-head"><strong>Needs Your Attention</strong><FaBell style={{ color: "#94a3b8", fontSize: 14 }} /></div>
        <div className="da-attention-list">
          {attentionItems.length ? attentionItems.map((item) => (
            <button key={item.key} type="button" className={`da-attention-row da-attention-row--${item.tone}`} onClick={item.action}>
              <span className="da-attention-icon"><i className={`fa-solid ${item.icon}`} aria-hidden="true"></i></span>
              <span className="da-attention-text">{item.text}</span>
              <i className="fa-solid fa-chevron-right da-attention-arrow" aria-hidden="true"></i>
            </button>
          )) : (
            <div className="da-attention-row da-attention-row--success" style={{ cursor: "default" }}>
              <span className="da-attention-icon"><i className="fa-solid fa-circle-check" aria-hidden="true"></i></span>
              <span className="da-attention-text">Nothing needs attention right now.</span>
            </div>
          )}
        </div>
      </div>

      {/* Main analytics row (section 8) */}
      <div className="tlp-grid2">
        <div className="tlp-card">
          <div className="tlp-card-head">
            <strong>Workload Trend</strong>
            <div className="da-pill-group da-pill-group--sm" role="group" aria-label="Trend granularity">
              {[["daily", "Daily"], ["weekly", "Weekly"], ["monthly", "Monthly"]].map(([key, label]) => (
                <button key={key} type="button" aria-pressed={trendGranularity === key} className={`da-pill-btn${trendGranularity === key ? " da-pill-btn--active" : ""}`} onClick={() => setTrendGranularity(key)}>{label}</button>
              ))}
            </div>
          </div>
          {busy && !allItems.length ? <div className="tlp-empty"><Spinner animation="border" size="sm" /></div>
            : !trendChart.hasAnyData ? (
              <div className="tlp-empty da-empty-compact"><div className="tlp-empty-icon"><FaChartLine /></div><p>No workload data in the current view.</p></div>
            ) : (
              <div className="da-chartbox"><Line data={trendChart} options={trendChartOpts} /></div>
            )}
        </div>
        <div className="tlp-card">
          <div className="tlp-card-head"><strong>Status Breakdown</strong></div>
          {busy && !allItems.length ? <div className="tlp-empty"><Spinner animation="border" size="sm" /></div>
            : statusRows.length > 1 ? (
              <>
                <div className="da-chartbox" style={{ height: 200 }}>
                  <div className="da-donut-center"><span>Total</span><strong>{statusTotal}</strong></div>
                  <Pie data={donutData} options={donutOpts} />
                </div>
                <div className="da-donut-legend">
                  {statusRows.map((r) => (
                    <div className="da-donut-legend-row" key={r.key}>
                      <span className="da-donut-dot" style={{ background: STATUS_COLOR[r.key] || "#94a3b8" }} />
                      <span className="tlp-row-title" style={{ flex: 1, fontSize: 12.5 }}>{r.label}</span>
                      <span style={{ fontWeight: 800, fontSize: 12.5 }}>{r.count}</span>
                      <span style={{ fontSize: 11, fontWeight: 700, color: "#64748b", minWidth: 36, textAlign: "right" }}>{statusTotal ? Math.round((r.count / statusTotal) * 100) : 0}%</span>
                    </div>
                  ))}
                </div>
              </>
            ) : statusRows.length === 1 ? (
              <div className="tlp-empty da-empty-compact">
                <div className="tlp-empty-icon"><FaCheckCircle /></div>
                <p>All {statusTotal} item{statusTotal === 1 ? "" : "s"} in this view are currently <strong>{statusRows[0].label}</strong>.</p>
              </div>
            ) : (
              <div className="tlp-empty da-empty-compact"><div className="tlp-empty-icon"><FaInbox /></div><p>No items match the current filters.</p></div>
            )}
        </div>
      </div>

      {/* Recurring Issues (section 9) */}
      <div className="tlp-card">
        <div className="tlp-card-head"><strong>Recurring Issues</strong></div>
        {recurringIssues.length ? (
          <div className="da-recurring-list">
            {recurringIssues.map((r) => (
              <div className="da-recurring-row" key={r.key}>
                <div className="da-recurring-main">
                  <span className="tlp-row-title">{r.name}</span>
                  <span className="da-recurring-count">{r.count} occurrence{r.count === 1 ? "" : "s"}{r.trendPct !== null && ` · ${r.trendPct > 0 ? "↑" : r.trendPct < 0 ? "↓" : "→"} ${Math.abs(r.trendPct)}% vs previous period`}</span>
                </div>
                <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => { setFilterCategory(r.key); setActivityTab("complaints"); jumpToActivity(); }}>View Related Complaints</Button>
              </div>
            ))}
          </div>
        ) : <div className="tlp-empty da-empty-compact"><div className="tlp-empty-icon"><FaInbox /></div><p>Not enough data yet to identify recurring issues.</p></div>}
      </div>

      {/* Issues by Category (section 10) */}
      <div className="tlp-card">
        <div className="tlp-card-head"><strong>Issues by Category</strong><FaTags style={{ color: "#94a3b8", fontSize: 14 }} /></div>
        <div className="da-category-list">
          {categoryRows.length ? categoryRows.map((cat) => {
            const active = filterCategory === cat.key;
            return (
              <button type="button" key={cat.key} className={`da-category-row${active ? " da-category-row--active" : ""}`} aria-pressed={active} onClick={() => setFilterCategory(active ? "ALL" : cat.key)}>
                <div className="da-category-row-top">
                  <span className="tlp-row-title">{cat.name}</span>
                  <span className="da-category-count">{cat.count} · {cat.pct}%{cat.trendPct !== null && ` · ${cat.trendPct > 0 ? "↑" : cat.trendPct < 0 ? "↓" : "→"} ${Math.abs(cat.trendPct)}%`}</span>
                </div>
                <div className="da-category-track"><div className="da-category-fill" style={{ width: `${cat.pct}%` }} /></div>
              </button>
            );
          }) : <div className="tlp-empty da-empty-compact"><div className="tlp-empty-icon"><FaTags /></div><p>No category data yet.</p></div>}
        </div>
      </div>

      {/* SLA & Aging (section 11) */}
      <div className="tlp-card">
        <div className="tlp-card-head"><strong>SLA &amp; Aging</strong></div>
        <div className="da-aging-grid">
          {agingBuckets.map((b) => (
            <div className="da-aging-bucket" key={b.key}>
              <span className="da-aging-count">{b.count}</span>
              <span className="da-aging-label">{b.label}</span>
            </div>
          ))}
        </div>
        <div className="da-sla-metrics">
          <div><span>SLA Compliance</span><strong>{slaCompliancePct === null ? "—" : `${slaCompliancePct}%`}</strong></div>
          <div><span>Average Resolution</span><strong>{avgResolutionMs === null ? "—" : formatDuration(avgResolutionMs)}</strong></div>
          <div><span>Median Resolution</span><strong>{medianResolutionMs === null ? "—" : formatDuration(medianResolutionMs)}</strong></div>
          <div><span>Overdue Rate</span><strong>{overdueRate}%</strong></div>
          <div><span>Oldest Open Item</span><strong>{oldestOpenItem ? `${daysBetween(new Date(oldestOpenItem.created_at), now)}d` : "—"}</strong></div>
        </div>
      </div>

      {/* Data Quality (section 12) */}
      <div className="tlp-card">
        <div className="tlp-card-head"><strong>Data Quality</strong>{unclassifiedPct > 20 && <Badge bg={null} className="tlp-pill tlp-pill-amber">Needs Review</Badge>}</div>
        <div className="da-dq-grid">
          <div className={`da-dq-tile${missingProjectCount > 0 ? " da-dq-tile--warn" : ""}`}><span>Project</span><strong>{missingProjectCount}</strong><small>missing</small></div>
          <div className={`da-dq-tile${missingModuleCount > 0 ? " da-dq-tile--warn" : ""}`}><span>Module</span><strong>{missingModuleCount}</strong><small>missing</small></div>
          <div className={`da-dq-tile${missingPriorityCount > 0 ? " da-dq-tile--warn" : ""}`}><span>Priority</span><strong>{missingPriorityCount}</strong><small>missing</small></div>
          <div className={`da-dq-tile${missingCategoryCount > 0 ? " da-dq-tile--warn" : ""}`}><span>Category</span><strong>{missingCategoryCount}</strong><small>missing</small></div>
        </div>
        <div className="da-dq-footer">
          <span>{unclassifiedPct}% of records in this view are incomplete</span>
          <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => { setUnclassifiedOnly(true); jumpToActivity(); }}>Review Incomplete Records</Button>
        </div>
      </div>

      {/* Recent Activity (section 13) */}
      <div className="tlp-card" ref={recentActivityRef}>
        <div className="tlp-card-head da-ch-wrap">
          <strong>Recent Activity</strong>
          <div className="da-toolbar-actions">
            <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" disabled={!isOnline} onClick={handleExportCsv} title="Export CSV"><FaFileCsv /></Button>
            <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" disabled={!isOnline} onClick={handleExportExcel} title="Export Excel"><FaFileExcel /></Button>
          </div>
        </div>
        <div className="da-tabs" role="group" aria-label="Filter recent activity">
          {TAB_OPTIONS.map(([key, label]) => (
            <button key={key} type="button" aria-pressed={activityTab === key} className={`da-tab${activityTab === key ? " da-tab--active" : ""}`} onClick={() => setActivityTab(key)}>{label}</button>
          ))}
        </div>
        {unclassifiedOnly && (
          <div className="da-active-chip">
            Showing unclassified records only
            <button type="button" onClick={() => setUnclassifiedOnly(false)} aria-label="Clear unclassified filter"><i className="fa-solid fa-xmark" aria-hidden="true"></i></button>
          </div>
        )}
        <div className="da-ch-wrap" style={{ padding: "10px 18px 0" }}>
          <InputGroup className="tlp-search">
            <Form.Control placeholder="Search title, project, module, category…" aria-label="Search recent activity" value={activitySearch} onChange={(e) => setActivitySearch(e.target.value)} />
          </InputGroup>
          <Form.Select className="tlp-select" aria-label="Sort recent activity" value={activitySort} onChange={(e) => setActivitySort(e.target.value)}>
            <option value="newest">Sort: Newest</option>
            <option value="oldest">Sort: Oldest</option>
            <option value="priority">Sort: Priority</option>
            <option value="due">Sort: Due Date</option>
            <option value="longest_pending">Sort: Longest Pending</option>
          </Form.Select>
        </div>

        {busy && !allItems.length ? (
          <div className="da-skeleton-table">{Array.from({ length: 5 }).map((_, i) => <div key={i} className="da-skeleton-row" />)}</div>
        ) : !activityPageRows.length ? (
          <div className="tlp-empty da-empty-compact">
            <div className="tlp-empty-icon"><FaInbox /></div>
            <p>{activitySearch ? "No records match your search." : allItems.length ? "No records in this view." : "No tickets or complaints yet."}</p>
            {!allItems.length && <Button className="tlp-btn tlp-btn-primary tlp-btn-sm" onClick={() => navigate("/data-analyst/tasks/create")}>Create Ticket</Button>}
          </div>
        ) : (
          <div className="unit-table-wrapper">
            <Table responsive className="unit-table tlp-table tlp-table-fixed">
              <colgroup><col style={{ width: "22%" }} /><col style={{ width: "9%" }} /><col style={{ width: "16%" }} /><col style={{ width: "10%" }} /><col style={{ width: "13%" }} /><col style={{ width: "12%" }} /><col style={{ width: "13%" }} /><col style={{ width: "70px" }} /></colgroup>
              <thead>
                <tr><th>Title</th><th>Type</th><th>Project / Module</th><th className="text-center">Priority</th><th>Status</th><th className="text-center">Created / Age</th><th className="text-center">Due / SLA</th><th className="text-center">Action</th></tr>
              </thead>
              <tbody>
                {activityPageRows.map((row) => {
                  const tat = getTatInfo(row, now);
                  const rowClass = tat.tone === "red" ? "da-row-danger" : tat.tone === "amber" ? "da-row-warning" : "";
                  const age = row.created_at ? daysBetween(new Date(row.created_at), now) : null;
                  return (
                    <tr key={`${row.itemType}-${row.id}`} className={rowClass}>
                      <td><span className="tlp-row-title tlp-ellipsis" title={row.title || "-"}>{row.title || "-"}</span></td>
                      <td className="small"><Badge bg={null} className={row.itemType === "ticket" ? "tlp-pill tlp-pill-blue" : "tlp-pill tlp-pill-gray"}>{row.itemType === "ticket" ? "Ticket" : "Complaint"}</Badge></td>
                      <td className="small">
                        <span className={`tlp-ellipsis${row.project_name ? "" : " da-unclassified"}`}>{row.project_name || "Unclassified"}</span>
                        <span className={`tlp-ellipsis${row.project_module_name ? "" : " da-unclassified"}`} style={{ fontSize: 11, color: "#94a3b8" }}>{row.project_module_name || "Unclassified"}</span>
                      </td>
                      <td className="text-center">{row.priority ? <PriorityPill priority={row.priority} /> : <span className="da-unclassified small">Unclassified</span>}</td>
                      <td><StatusPill status={row.status} /></td>
                      <td className="small text-center">{formatDate(row.created_at)}{age !== null && <div className="da-age">{age}d</div>}</td>
                      <td className="small text-center">
                        <div>{formatDate(row.due_at)}</div>
                        {row.due_at && <span className={`tlp-tat tlp-tat-${tat.tone}`}>{tat.label}</span>}
                      </td>
                      <td className="text-center">
                        <div className="tlp-actions" style={{ justifyContent: "center" }}>
                          <Button size="sm" variant="outline-primary" className="da-action-btn" title="View" aria-label="View" onClick={() => navigate(`/${user?.role}/tasks/${row.id}`)}><FaEye /></Button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          </div>
        )}

        {!busy && activitySorted.length > 0 && (
          <div className="tlp-pagination">
            <span className="tlp-pagination-info">Showing {(activityPageClamped - 1) * RECENT_PAGE_SIZE + 1}–{Math.min(activityPageClamped * RECENT_PAGE_SIZE, activitySorted.length)} of {activitySorted.length}</span>
            <div className="tlp-pagination-controls">
              <Button size="sm" variant="outline-secondary" disabled={activityPageClamped <= 1} onClick={() => setActivityPage(activityPageClamped - 1)} aria-label="Previous page">‹</Button>
              <span className="tlp-pagination-page">{activityPageClamped} / {activityTotalPages}</span>
              <Button size="sm" variant="outline-secondary" disabled={activityPageClamped >= activityTotalPages} onClick={() => setActivityPage(activityPageClamped + 1)} aria-label="Next page">›</Button>
            </div>
            <div className="da-viewall-links">
              <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => navigate("/data-analyst/my-tasks")}>View All Tasks <FaArrowRight style={{ fontSize: 10 }} /></Button>
              <Button className="tlp-btn tlp-btn-ghost tlp-btn-sm" onClick={() => navigate("/data-analyst/complaints")}>View All Complaints <FaArrowRight style={{ fontSize: 10 }} /></Button>
            </div>
          </div>
        )}
      </div>

      {/* Quick Actions + Forms (sections 15-16) -- de-emphasized, bottom of page */}
      <div className="tlp-grid2">
        <div className="tlp-card">
          <div className="tlp-card-head"><strong>Quick Actions</strong></div>
          <div>
            <div className="tlp-formrow" role="button" onClick={() => navigate("/data-analyst/tasks/create")}>
              <div className="tlp-formrow-main"><div className="tlp-formrow-icon"><FaPlus /></div><div className="tlp-formrow-text"><span className="tlp-formrow-title">Create Ticket</span><span className="tlp-formrow-sub">Log a new support ticket</span></div></div>
              <FaArrowRight className="tlp-formrow-arrow" />
            </div>
            <div className="tlp-formrow" role="button" onClick={() => navigate("/data-analyst/tasks/raise-complaint")}>
              <div className="tlp-formrow-main"><div className="tlp-formrow-icon"><FaExclamationCircle /></div><div className="tlp-formrow-text"><span className="tlp-formrow-title">Raise Complaint</span><span className="tlp-formrow-sub">Report an issue for review</span></div></div>
              <FaArrowRight className="tlp-formrow-arrow" />
            </div>
            <Can moduleKey="form_assignments" actionKey="create">
              <div className="tlp-formrow" role="button" onClick={() => navigate("/data-analyst/form-assignments")}>
                <div className="tlp-formrow-main"><div className="tlp-formrow-icon"><FaClipboardList /></div><div className="tlp-formrow-text"><span className="tlp-formrow-title">Assign Form</span><span className="tlp-formrow-sub">Send a form for completion</span></div></div>
                <FaArrowRight className="tlp-formrow-arrow" />
              </div>
            </Can>
            <details className="da-more-actions">
              <summary><FaEllipsisH style={{ fontSize: 11, marginRight: 6 }} />More Actions</summary>
              <div className="tlp-formrow" role="button" onClick={() => navigate("/data-analyst/my-tasks")}>
                <div className="tlp-formrow-main"><div className="tlp-formrow-icon"><FaTicketAlt /></div><div className="tlp-formrow-text"><span className="tlp-formrow-title">View My Tasks</span><span className="tlp-formrow-sub">Browse all assigned tickets</span></div></div>
                <FaArrowRight className="tlp-formrow-arrow" />
              </div>
              <div className="tlp-formrow" role="button" onClick={() => navigate("/data-analyst/complaints")}>
                <div className="tlp-formrow-main"><div className="tlp-formrow-icon"><FaHeadset /></div><div className="tlp-formrow-text"><span className="tlp-formrow-title">View Complaints</span><span className="tlp-formrow-sub">Browse all assigned complaints</span></div></div>
                <FaArrowRight className="tlp-formrow-arrow" />
              </div>
              <Can moduleKey="assigned_forms" actionKey="view_own">
                <div className="tlp-formrow" role="button" onClick={() => navigate("/data-analyst/my-assigned-forms")}>
                  <div className="tlp-formrow-main"><div className="tlp-formrow-icon"><FaClipboardList /></div><div className="tlp-formrow-text"><span className="tlp-formrow-title">Review Forms</span><span className="tlp-formrow-sub">Complete forms assigned to you</span></div></div>
                  <FaArrowRight className="tlp-formrow-arrow" />
                </div>
              </Can>
            </details>
          </div>
        </div>

        <div className="tlp-card">
          <div className="tlp-card-head"><strong>Forms</strong></div>
          <Can moduleKey="assigned_forms" actionKey="view_own">
            <div className="da-forms-summary da-forms-summary--single">
              <div><span>Pending Reviews</span><strong>{reviewFormsLoading ? "…" : reviewFormsCount}</strong></div>
            </div>
          </Can>
          <Can moduleKey="form_assignments" actionKey="create">
            <div className="tlp-formrow" role="button" onClick={() => navigate("/data-analyst/form-assignments")}>
              <div className="tlp-formrow-main"><div className="tlp-formrow-icon"><FaFolderOpen /></div><div className="tlp-formrow-text"><span className="tlp-formrow-title">Assign Forms</span><span className="tlp-formrow-sub">Send forms to team members</span></div></div>
              <FaArrowRight className="tlp-formrow-arrow" />
            </div>
          </Can>
          <Can moduleKey="assigned_forms" actionKey="view_own">
            <div className="tlp-formrow" role="button" onClick={() => navigate("/data-analyst/my-assigned-forms")}>
              <div className="tlp-formrow-main"><div className="tlp-formrow-icon"><FaClipboardList /></div><div className="tlp-formrow-text"><span className="tlp-formrow-title">Review Forms</span><span className="tlp-formrow-sub">Complete forms assigned to you</span></div></div>
              <FaArrowRight className="tlp-formrow-arrow" />
            </div>
          </Can>
        </div>
      </div>

    </div>
  );
}
